import { Fragment, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { useAppStore, type ThemeMode } from '@repletoryreach/sdk-utils/store';
import { useT, useI18n } from '../../i18n/I18nProvider';
import { LOCALES, type Locale } from '../../i18n/catalog';
import { BrandLogo } from '../../components/BrandLogo';
import { BrandWordmark } from '../../components/BrandWordmark';
import { DOCS_URL, STATUS_URL } from '../../app/external-links';
import { BookIcon, ContrastIcon, GlobeIcon, ShieldIcon, StatusIcon } from './components/icons';
import './auth.css';

const THEME_MODES: ThemeMode[] = ['light', 'dark', 'system'];

export default function AuthLayout() {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const themeMode = useAppStore((s) => s.themeMode);
  const setThemeMode = useAppStore((s) => s.setThemeMode);

  // Only the links that are configured are offered. A hardcoded
  // docs.repletoryreach host resolves nowhere, and a dead link in the footer
  // of a sign-in screen is worse than no link at all.
  const footerLinks = [
    DOCS_URL !== '' && { href: DOCS_URL, label: t('auth.footer.docs'), icon: <BookIcon /> },
    STATUS_URL !== '' && { href: STATUS_URL, label: t('auth.footer.status'), icon: <StatusIcon /> }
  ].filter(Boolean) as Array<{ href: string; label: string; icon: ReactNode }>;

  return (
    <div className="admin-auth">
      {/*
        The brand panel is navy in both themes by design, like the navigation
        rail: it is chrome rather than content. `auth.css` carries the note and
        the literals; nothing else on this screen escapes the theme.
      */}
      <aside className="admin-auth__panel">
        <div className="admin-auth__brand">
          <BrandLogo size={56} />
          <BrandWordmark />
        </div>

        <span className="admin-auth__pill">
          <ShieldIcon size={17} />
          {t('auth.panel.badge')}
        </span>

        <span className="admin-auth__rule" aria-hidden />
        <p className="admin-auth__tagline">{t('auth.panel.tagline')}</p>

        <div className="admin-auth__panel-foot">
          <span className="admin-auth__picker">
            <GlobeIcon size={17} />
            <select
              aria-label={t('common.language')}
              value={locale}
              onChange={(e) => setLocale(e.currentTarget.value as Locale)}
            >
              {LOCALES.map((l) => (
                // i18n-ignore: each language is named in its own language
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </span>
          <span className="admin-auth__picker-divider" aria-hidden />
          <span className="admin-auth__picker">
            <ContrastIcon size={17} />
            <select
              aria-label={t('auth.panel.theme')}
              value={themeMode}
              onChange={(e) => setThemeMode(e.currentTarget.value as ThemeMode)}
            >
              {THEME_MODES.map((mode) => (
                <option key={mode} value={mode}>{t(`auth.panel.theme.${mode}`)}</option>
              ))}
            </select>
          </span>
        </div>
      </aside>

      <main className="admin-auth__main">
        {/* The routed screen owns everything inside the card, including its
            own heading: the wrong-domain notice belongs under the sign-in
            title rather than above it, and only sign-in can receive it. */}
        <div className="admin-auth__card">
          <Outlet />
        </div>

        {footerLinks.length > 0 && (
          <nav className="admin-auth__footer">
            {footerLinks.map((link, i) => (
              <Fragment key={link.href}>
                {i > 0 && <span className="admin-auth__footer-divider" aria-hidden />}
                <a href={link.href} target="_blank" rel="noreferrer">
                  {link.icon}
                  {link.label}
                </a>
              </Fragment>
            ))}
          </nav>
        )}
      </main>
    </div>
  );
}
